import { useState } from 'react'
import { setApiBaseUrl, testApiConnection, getCurrentApiUrl } from '../../services/apiConfig'

interface ApiConfigDialogProps {
  isOpen: boolean
  onClose: () => void
  onSuccess?: () => void
}

export default function ApiConfigDialog({ isOpen, onClose, onSuccess }: ApiConfigDialogProps) {
  const [apiUrl, setApiUrl] = useState(() => {
    const current = getCurrentApiUrl()
    // إزالة /api من النهاية للعرض
    return current.replace('/api', '').replace(/\/$/, '')
  })
  const [testing, setTesting] = useState(false)
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null)
  const [saving, setSaving] = useState(false)

  if (!isOpen) return null

  const handleTest = async () => {
    if (!apiUrl.trim()) {
      setTestResult({ success: false, message: 'يرجى إدخال عنوان API' })
      return
    }

    setTesting(true)
    setTestResult(null)

    try {
      const result = await testApiConnection(apiUrl)
      setTestResult(result)
    } catch (error: any) {
      setTestResult({ success: false, message: error.message || 'خطأ غير معروف' })
    } finally {
      setTesting(false)
    }
  }

  const handleSave = async () => {
    if (!apiUrl.trim()) {
      setTestResult({ success: false, message: 'يرجى إدخال عنوان API' })
      return
    }

    // اختبار الاتصال أولاً
    setTesting(true)
    setTestResult(null)

    try {
      const testResult = await testApiConnection(apiUrl)
      setTestResult(testResult)

      if (testResult.success) {
        setSaving(true)
        setApiBaseUrl(apiUrl)
        // سيتم إعادة تحميل الصفحة تلقائياً في setApiBaseUrl
        onSuccess?.()
      }
    } catch (error: any) {
      setTestResult({ success: false, message: error.message || 'خطأ غير معروف' })
    } finally {
      setTesting(false)
      setSaving(false)
    }
  }

  const handleCancel = () => {
    setApiUrl(getCurrentApiUrl().replace('/api', '').replace(/\/$/, ''))
    setTestResult(null)
    onClose()
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
        <h2 className="text-xl font-bold mb-4 text-gray-900">
          إعدادات عنوان API
        </h2>
        
        <p className="text-sm text-gray-600 mb-4">
          أدخل عنوان السيرفر (API Server) للاتصال به. يمكنك استخدام:
          <br />• عنوان IP: <code className="bg-gray-100 px-1 rounded">192.168.1.100:57688</code>
          <br />• Domain: <code className="bg-gray-100 px-1 rounded">api.example.com</code>
          <br />• URL كامل: <code className="bg-gray-100 px-1 rounded">http://api.example.com</code>
        </p>

        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            عنوان API Server
          </label>
          <input
            type="text"
            value={apiUrl}
            onChange={(e) => setApiUrl(e.target.value)}
            placeholder="مثال: 192.168.1.100:57688 أو api.example.com"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            disabled={testing || saving}
          />
        </div>

        {testResult && (
          <div
            className={`mb-4 p-3 rounded-md text-sm ${
              testResult.success
                ? 'bg-green-50/20 text-green-800'
                : 'bg-red-50/20 text-red-800'
            }`}
          >
            {testResult.message}
          </div>
        )}

        <div className="flex gap-2 justify-end">
          <button
            onClick={handleCancel}
            className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 transition-colors"
            disabled={testing || saving}
          >
            إلغاء
          </button>
          <button
            onClick={handleTest}
            className="px-4 py-2 text-white bg-blue-500 rounded-md hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={testing || saving || !apiUrl.trim()}
          >
            {testing ? 'جاري الاختبار...' : 'اختبار الاتصال'}
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 text-white bg-green-500 rounded-md hover:bg-green-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={testing || saving || !apiUrl.trim() || (testResult && !testResult.success)}
          >
            {saving ? 'جاري الحفظ...' : 'حفظ'}
          </button>
        </div>
      </div>
    </div>
  )
}


